import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import IotVisionBreadcrumbs, { IBreadcrumbItem } from "components/common/IotVisionBreadcrumbs";
import IotVisionSegmentedTabs, { ISegmentedTabOption } from "components/common/IotVisionSegmentedTabs";
import LiveRefreshStatus from "components/common/LiveRefreshStatus";
import IotVisionSelectDropdown, { ISelectDropdownOption } from "components/dropdowns/IotVisionSelectDropdown";
import HighlightedSectionHeading from "components/headings/HighlightedSectionHeading";
import PaperWrapper from "components/wrappers/PaperWrapper";
import { AppTitlePrefix } from "constants/app.constants";
import { ALL_FILTER_VALUE, EM_DASH } from "constants/schedule-monitor.constants";
import { getScheduleStatusDisplay, getScheduleTypeDisplay, scheduleTypeDisplayMap } from "configs/schedules";
import { ScheduleStatus, ScheduleType } from "enums/schedule.enum";
import { TaskStatus } from "enums/task.enum";
import { SCHEDULES_PAGE_SIZE } from "repositories/schedules/schedules.repository";
import { ITaskRowViewModel } from "types/task";
import deriveScheduleStatus, { DerivedScheduleStatus } from "utils/derive-schedule-status";
import ScheduleKpiCards, { IScheduleKpiCardDescriptor } from "./schedule-kpi-cards";
import ScheduleTable from "./schedule-table";
import useAllSchedules from "./use-all-schedules";
import useLatestTaskByScheduleId from "./use-latest-task-by-schedule-id";

const BREADCRUMB_ITEMS: IBreadcrumbItem[] = [{ label: "Admin" }, { label: "Schedules" }, { label: "Health check" }];

type ScheduleStatusFilter = ScheduleStatus | DerivedScheduleStatus | typeof ALL_FILTER_VALUE;
type ScheduleTypeFilter = ScheduleType | typeof ALL_FILTER_VALUE;

const STATUS_TAB_VALUES: ScheduleStatusFilter[] = [
    ALL_FILTER_VALUE,
    ScheduleStatus.ACTIVE,
    ScheduleStatus.SUSPENDED,
    ScheduleStatus.EXPIRED,
    DerivedScheduleStatus.FAILED,
    DerivedScheduleStatus.STALLED,
];

const TYPE_OPTIONS: ISelectDropdownOption<ScheduleTypeFilter>[] = [
    { value: ALL_FILTER_VALUE, label: "All" },
    ...Object.values(ScheduleType).map((type) => ({
        value: type,
        label: scheduleTypeDisplayMap[type].label,
    })),
];

const isScheduleStatus = (value: string | null): value is ScheduleStatusFilter =>
    value !== null &&
    (value === ALL_FILTER_VALUE ||
        (Object.values(ScheduleStatus) as string[]).includes(value) ||
        (Object.values(DerivedScheduleStatus) as string[]).includes(value));

const isScheduleType = (value: string | null): value is ScheduleTypeFilter =>
    value !== null && (value === ALL_FILTER_VALUE || (Object.values(ScheduleType) as string[]).includes(value));

/**
 * Every status tab - including the API-native ones like `active` - must filter on the derived
 * status, not the raw API status: an API-active schedule that's actually failed/stalled belongs
 * in those tabs instead, so `active` can't just mean "API says active". The backend has no
 * notion of derived status, so all of these tabs paginate client-side over the fully-drained
 * set instead of a server-side filter.
 */
const isFilterableStatus = (value: ScheduleStatusFilter): value is ScheduleStatus | DerivedScheduleStatus =>
    value !== ALL_FILTER_VALUE;

const ScheduleMonitorPage = (): JSX.Element => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const statusParam = searchParams.get("status");
    const typeParam = searchParams.get("type");
    const status = isScheduleStatus(statusParam) ? statusParam : ALL_FILTER_VALUE;
    const type = isScheduleType(typeParam) ? typeParam : ALL_FILTER_VALUE;

    // Seed defaults into the URL once on mount. `status`/`type` above already fall back
    // locally, so every render (including the very first) has both required selectors
    // regardless of when this URL patch commits - no request is ever sent incomplete.
    useEffect(() => {
        if (statusParam === status && typeParam === type) return;
        const next = new URLSearchParams(searchParams);
        next.set("status", status);
        next.set("type", type);
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = (nextStatus: ScheduleStatusFilter) => {
        const next = new URLSearchParams(searchParams);
        next.set("status", nextStatus);
        if (nextStatus === ALL_FILTER_VALUE) {
            next.set("type", ALL_FILTER_VALUE);
        }
        setSearchParams(next);
    };

    const handleTypeChange = (nextType: ScheduleTypeFilter) => {
        const next = new URLSearchParams(searchParams);
        next.set("type", nextType);
        setSearchParams(next);
    };

    const handleForbidden = useCallback(() => navigate("/forbidden", { replace: true }), [navigate]);
    const handleNotFound = useCallback(() => navigate("page-not-found", { replace: true }), [navigate]);

    // The complete, unpaginated schedule set. Every status tab - including the API-native ones -
    // filters and paginates over this set client-side, since only the derived status (not the
    // raw API status) is a correct partition; see `isFilterableStatus` above.
    const allSchedules = useAllSchedules({ onForbidden: handleForbidden, onNotFound: handleNotFound });

    const typeFilteredAllSchedules = useMemo(
        () =>
            type === ALL_FILTER_VALUE ? allSchedules.rows : allSchedules.rows.filter((row) => row.typeCode === type),
        [allSchedules.rows, type],
    );

    // The KPI cards need every schedule's derived status and task data regardless of which tab
    // or type filter is currently selected, so the whole drained set's task data is always
    // fetched here - see `use-latest-task-by-schedule-id.ts` for why this isn't polled.
    const scheduleIdsNeedingTaskData = useMemo(
        () => allSchedules.rows.map((row) => row.id),
        [allSchedules.rows],
    );
    const {
        tasksByScheduleId,
        recentTasksByScheduleId,
        loading: tasksLoading,
        error: tasksError,
    } = useLatestTaskByScheduleId(scheduleIdsNeedingTaskData, allSchedules.refreshedAt);

    const derivedStatusById = useMemo(() => {
        const now = new Date();
        const map: Record<string, string> = {};
        allSchedules.rows.forEach((row) => {
            map[row.id] = deriveScheduleStatus(row, tasksByScheduleId[row.id] ?? null, now);
        });
        return map;
    }, [allSchedules.rows, tasksByScheduleId]);

    // Cards degrade individually rather than as a block: only the "total" half of the Active
    // card's ratio can be known from the drained set alone - "active" is a derived count (see
    // below), so it and failed/stalled/success-rate all also need task data. Each figure is
    // gated on exactly the inputs it reads. A zero denominator (no schedules/tasks yet) is
    // "incomplete", not "0%".
    const schedulesReady = !allSchedules.loading && !allSchedules.error;
    const tasksReady = !tasksLoading && !tasksError;
    const derivedReady = schedulesReady && tasksReady;

    // Whether the worker has actually enqueued a *future* run for this schedule. Only meaningful
    // for schedules the API still reports as active - a suspended/expired schedule having no
    // queued task is expected, not a health signal, so it stays null (chip hidden) there.
    // `null` while task data hasn't loaded yet, so the chip never flashes "no next task"
    // ahead of the real answer.
    const hasNextTaskById = useMemo(() => {
        const now = new Date();
        const map: Record<string, boolean | null> = {};
        allSchedules.rows.forEach((row) => {
            if (row.status !== ScheduleStatus.ACTIVE || !tasksReady) {
                map[row.id] = null;
                return;
            }
            // A pending/queued task whose scheduled run is already due isn't a next task ahead
            // of the schedule - it's a stalled task for a past tick, the same symptom
            // `isUnstarted` in derive-schedule-status.ts treats as no task at all. Only a
            // pending/queued task scheduled strictly in the future counts as "has a next task".
            map[row.id] = (recentTasksByScheduleId[row.id] ?? []).some(
                (task) =>
                    (task.statusCode === TaskStatus.PENDING || task.statusCode === TaskStatus.QUEUED) &&
                    task.scheduledDateTime !== null &&
                    task.scheduledDateTime > now,
            );
        });
        return map;
    }, [allSchedules.rows, recentTasksByScheduleId, tasksReady]);

    const totalSchedules = allSchedules.rows.length;
    // "Active" here means still active after derivation - an API-active schedule that's been
    // overridden to failed/stalled/unknown no longer counts, since deriveScheduleStatus only
    // ever narrows an active schedule to one of those, never the other way round.
    const activeCount = useMemo(
        () => allSchedules.rows.filter((row) => derivedStatusById[row.id] === ScheduleStatus.ACTIVE).length,
        [allSchedules.rows, derivedStatusById],
    );
    // The Failed card's denominator is every schedule still "in play" by raw API status
    // (active, including those narrowed to failed/stalled) - unlike `activeCount` above, this
    // must NOT exclude failed/stalled schedules, since those are what the card counts against it.
    const inPlayCount = useMemo(
        () => allSchedules.rows.filter((row) => row.status === ScheduleStatus.ACTIVE).length,
        [allSchedules.rows],
    );
    const failedCount = useMemo(
        () => allSchedules.rows.filter((row) => derivedStatusById[row.id] === DerivedScheduleStatus.FAILED).length,
        [allSchedules.rows, derivedStatusById],
    );
    const stalledCount = useMemo(
        () => allSchedules.rows.filter((row) => derivedStatusById[row.id] === DerivedScheduleStatus.STALLED).length,
        [allSchedules.rows, derivedStatusById],
    );
    const successRatePct = useMemo(() => {
        const latestTasks = allSchedules.rows
            .map((row) => tasksByScheduleId[row.id])
            .filter((task): task is ITaskRowViewModel => Boolean(task));
        if (!latestTasks.length) return null;
        const succeeded = latestTasks.filter((task) => task.statusCode === TaskStatus.SUCCESS).length;
        return Math.round((succeeded / latestTasks.length) * 100);
    }, [allSchedules.rows, tasksByScheduleId]);

    const kpiCards: IScheduleKpiCardDescriptor[] = useMemo(
        () => [
            {
                key: "failed",
                title: "Failed Schedules",
                icon: <WarningAmberIcon />,
                accentColor: "error",
                value: derivedReady ? String(failedCount) : EM_DASH,
                valueSuffix: `of ${derivedReady ? inPlayCount : EM_DASH} active`,
                description: "Most recent task failed",
                tooltip:
                    "Active schedules whose most recently due task failed, out of all schedules that " +
                    "aren't suspended or expired.",
            },
            {
                key: "stalled",
                title: "Stalled Schedules",
                icon: <AccessTimeIcon />,
                accentColor: "warning",
                value: derivedReady ? String(stalledCount) : EM_DASH,
                valueSuffix: "schedules",
                description: "Overdue against cadence, no task run",
                tooltip:
                    "Active schedules that are overdue for their next run, with no task recorded for that run.",
            },
            {
                key: "successRate",
                title: "Task Success Rate",
                icon: <CheckCircleOutlineIcon />,
                accentColor: "success",
                value: derivedReady && successRatePct !== null ? String(successRatePct) : EM_DASH,
                valueSuffix: "%",
                description: "Schedules whose latest task succeeded",
                tooltip: "Share of schedules whose most recent task succeeded.",
            },
            {
                key: "active",
                title: "Active Schedules",
                icon: <EventAvailableIcon />,
                accentColor: "primary",
                value: derivedReady ? String(activeCount) : EM_DASH,
                valueSuffix: `of ${schedulesReady ? totalSchedules : EM_DASH} total`,
                description: "Across schedule types",
                tooltip: "Schedules currently active (and not failed/stalled), out of every schedule.",
            },
        ],
        [
            derivedReady,
            schedulesReady,
            activeCount,
            inPlayCount,
            failedCount,
            stalledCount,
            successRatePct,
            totalSchedules,
        ],
    );

    // Every tab - `ALL` included - filters over the drained set by derived status, so an
    // API-active schedule that's actually failed/stalled never shows up under `active`.
    const filteredRows = useMemo(
        () =>
            isFilterableStatus(status)
                ? typeFilteredAllSchedules.filter((row) => derivedStatusById[row.id] === status)
                : typeFilteredAllSchedules,
        [status, typeFilteredAllSchedules, derivedStatusById],
    );

    // All tabs paginate client-side over `filteredRows` since there's no backend cursor for a
    // status it doesn't know about - reset back to page one whenever the filter changes.
    const [pageIndex, setPageIndex] = useState(0);
    useEffect(() => {
        setPageIndex(0);
    }, [status, type]);

    const statusTabOptions: ISegmentedTabOption<ScheduleStatusFilter>[] = useMemo(
        () =>
            STATUS_TAB_VALUES.map((statusValue) => ({
                value: statusValue,
                label: statusValue === ALL_FILTER_VALUE ? "All" : getScheduleStatusDisplay(statusValue).label,
            })),
        [],
    );

    const tableRows = filteredRows.slice(pageIndex * SCHEDULES_PAGE_SIZE, (pageIndex + 1) * SCHEDULES_PAGE_SIZE);
    const tableTotal = filteredRows.length;
    const tableLoading = allSchedules.loading || tasksLoading;
    const tableError = allSchedules.error ?? tasksError;
    const tableHasPrevious = pageIndex > 0;
    const tableHasNext = (pageIndex + 1) * SCHEDULES_PAGE_SIZE < filteredRows.length;
    const handleTablePrevious = () => setPageIndex((i) => Math.max(0, i - 1));
    const handleTableNext = () => setPageIndex((i) => i + 1);

    return (
        <>
            <Helmet>
                <title>{AppTitlePrefix} | Schedules health</title>
            </Helmet>
            <IotVisionBreadcrumbs items={BREADCRUMB_ITEMS} sx={{ mb: 1 }} />
            <div className="sec-header">
                <HighlightedSectionHeading
                    heading="Schedules health"
                    icon={<EventRepeatIcon className="icon" />}
                />
            </div>
            <Typography color="text.secondary" fontSize="14px" variant="body2" sx={{ mb: 1 }}>
                Operational integrity of the scheduling pipeline. Failed schedules are active schedules whose most
                recent task failed; stalled schedules are active schedules overdue against their cadence with no
                task recorded for that run.
            </Typography>
            <Box sx={{ mb: 2 }}>
                <LiveRefreshStatus
                    lastRefreshedAt={allSchedules.lastRefreshedAt}
                    onRefresh={allSchedules.refresh}
                    refreshing={allSchedules.loading}
                    error={Boolean(allSchedules.error)}
                />
            </Box>
            <Box sx={{ mb: 2 }}>
                <ScheduleKpiCards cards={kpiCards} />
            </Box>
            <PaperWrapper>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <IotVisionSegmentedTabs
                            ariaLabel="schedule status"
                            value={status}
                            options={statusTabOptions}
                            onChange={handleStatusChange}
                        />
                    </Grid>
                    <Grid item>
                        <Tooltip
                            title={
                                status === ALL_FILTER_VALUE
                                    ? "Select a status to filter by type"
                                    : ""
                            }
                        >
                            <span>
                                <IotVisionSelectDropdown
                                    value={type}
                                    options={TYPE_OPTIONS}
                                    onChange={handleTypeChange}
                                    disabled={status === ALL_FILTER_VALUE}
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            borderRadius: "10px",
                                            "&:focus": {
                                                borderRadius: "10px",
                                            },
                                        },
                                    }}
                                />
                            </span>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <ScheduleTable
                        statusLabel={status === ALL_FILTER_VALUE ? "All" : getScheduleStatusDisplay(status).label}
                        typeLabel={type === ALL_FILTER_VALUE ? "All" : getScheduleTypeDisplay(type).label}
                        rows={tableRows}
                        derivedStatusById={derivedStatusById}
                        hasNextTaskById={hasNextTaskById}
                        total={tableTotal}
                        loading={tableLoading}
                        error={tableError}
                        hasPrevious={tableHasPrevious}
                        hasNext={tableHasNext}
                        onPrevious={handleTablePrevious}
                        onNext={handleTableNext}
                    />
                </Box>
            </PaperWrapper>
        </>
    );
};

export default ScheduleMonitorPage;
